import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { isPrerendering } from "./utils/isPrerendering";

/*
  Remove old page-specific SEO tags inherited from public/index.html.

  The reusable SEO.jsx component will insert the correct title,
  description, canonical, Open Graph, Twitter and schema tags
  for the current route.

  This prevents duplicate tags such as:
  - AI Career Resilience Audit
  - Career Intelligence Blog
*/
function removeLegacySeoTags() {
  if (typeof document === "undefined") {
    return;
  }

  const selectors = [
    'meta[name="description"]',
    'meta[name="keywords"]',

    'meta[property="og:title"]',
    'meta[property="og:description"]',
    'meta[property="og:image"]',
    'meta[property="og:url"]',
    'meta[property="og:type"]',
    'meta[property="og:site_name"]',

    'meta[name="twitter:card"]',
    'meta[name="twitter:title"]',
    'meta[name="twitter:description"]',
    'meta[name="twitter:image"]',
    'meta[name="twitter:site"]',
    'meta[name="twitter:creator"]',

    'link[rel="canonical"]',
  ];

  selectors.forEach((selector) => {
    document
      .querySelectorAll(selector)
      .forEach((element) => {
        /*
          Do not remove tags already created by react-helmet-async.
        */
        if (
          element.getAttribute("data-rh") !== "true"
        ) {
          element.remove();
        }
      });
  });

  /*
    Remove hard-coded structured data from public/index.html.

    SEO.jsx will insert the appropriate schema for each route.
  */
  document
    .querySelectorAll(
      'script[type="application/ld+json"]'
    )
    .forEach((element) => {
      if (
        element.getAttribute("data-rh") !== "true"
      ) {
        element.remove();
      }
    });

  /*
    Use a neutral fallback until SEO.jsx sets
    the correct route-specific title.
  */
  document.title = "Reignite Spirit";
}

removeLegacySeoTags();

/*
  ReactSnap opens public routes during npm run build.

  API, Supabase and backend requests are unnecessary during prerendering.
  Safe placeholder responses are returned so public pages can render.
*/
if (
  typeof window !== "undefined" &&
  isPrerendering() &&
  typeof window.fetch === "function"
) {
  const originalFetch =
    window.fetch.bind(window);

  window.fetch = async (
    input,
    init = {}
  ) => {
    const requestedUrl =
      typeof input === "string"
        ? input
        : input?.url || "";

    let absoluteUrl = requestedUrl;

    try {
      absoluteUrl = new URL(
        requestedUrl,
        window.location.origin
      ).href;
    } catch {
      // Keep original URL if it cannot be normalized.
    }

    const normalizedUrl = String(
      absoluteUrl || ""
    ).toLowerCase();

    const method = String(
      init?.method || "GET"
    ).toUpperCase();

    const isSupabaseRestRequest =
      normalizedUrl.includes(
        "supabase.co/rest/v1/"
      );

    const isSupabaseRequest =
      normalizedUrl.includes(
        "supabase.co"
      );

    const isApplicationApiRequest =
      normalizedUrl.includes("/api/") ||
      normalizedUrl.includes(
        "api.reignitespirit.com"
      ) ||
      normalizedUrl.includes(
        "localhost:5276"
      ) ||
      normalizedUrl.includes(
        "localhost:8080"
      ) ||
      normalizedUrl.includes(
        "103.104.48.64"
      );

    /*
      Supabase SELECT requests return arrays.

      Returning [] prevents errors such as:
      "map is not a function"
    */
    if (
      isSupabaseRestRequest &&
      method === "GET"
    ) {
      console.info(
        `[ReactSnap] Returned empty Supabase list: ${absoluteUrl}`
      );

      return new Response(
        JSON.stringify([]),
        {
          status: 200,
          statusText: "OK",
          headers: {
            "Content-Type":
              "application/json",
            "Content-Range": "0-0/0",
          },
        }
      );
    }

    /*
      Other Supabase requests receive a safe object.
    */
    if (isSupabaseRequest) {
      console.info(
        `[ReactSnap] Skipped Supabase request: ${absoluteUrl}`
      );

      return new Response(
        JSON.stringify({
          data: null,
          error: null,
        }),
        {
          status: 200,
          statusText: "OK",
          headers: {
            "Content-Type":
              "application/json",
          },
        }
      );
    }

    /*
      Skip application backend API calls
      during prerendering.
    */
    if (isApplicationApiRequest) {
      console.info(
        `[ReactSnap] Skipped API request: ${absoluteUrl}`
      );

      return new Response(
        JSON.stringify({
          prerender: true,
          data: null,
        }),
        {
          status: 200,
          statusText: "OK",
          headers: {
            "Content-Type":
              "application/json",
          },
        }
      );
    }

    /*
      Try loading normal local and static resources.

      External fonts may fail when third-party requests
      are blocked. Return a bodyless 204 response safely.
    */
    try {
      return await originalFetch(
        input,
        init
      );
    } catch (error) {
      console.warn(
        `[ReactSnap] Optional resource failed: ${absoluteUrl}`,
        error?.message || error
      );

      return new Response(null, {
        status: 204,
        statusText: "No Content",
      });
    }
  };
}

/*
  Auto-recover from chunk-loading errors after deployment.

  Skip this during ReactSnap because page reloads can interfere
  with static HTML generation.
*/
if (
  typeof window !== "undefined" &&
  !isPrerendering()
) {
  window.addEventListener(
    "error",
    (event) => {
      const message =
        event?.message || "";

      const isChunkError =
        message.includes(
          "Loading chunk"
        ) ||
        message.includes(
          "ChunkLoadError"
        ) ||
        message.includes(
          "Failed to fetch dynamically imported module"
        );

      if (!isChunkError) {
        return;
      }

      try {
        const reloadKey =
          "chunk-reload";

        if (
          !window.sessionStorage.getItem(
            reloadKey
          )
        ) {
          window.sessionStorage.setItem(
            reloadKey,
            "1"
          );

          window.location.reload();
        }
      } catch {
        window.location.reload();
      }
    }
  );

  /*
    Remove the reload flag once the application
    loads successfully.
  */
  window.addEventListener(
    "load",
    () => {
      try {
        window.sessionStorage.removeItem(
          "chunk-reload"
        );
      } catch {
        // Ignore unavailable session storage.
      }
    }
  );
}

const rootElement =
  document.getElementById("root");

if (!rootElement) {
  throw new Error(
    'Unable to start React: element with id="root" was not found.'
  );
}

const root =
  ReactDOM.createRoot(rootElement);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

reportWebVitals();